const CONTENT_API = 'https://api.dawwin.al-ilm.net/qirtaas/content'

export type VerseTafsir = { slug: string; name: string; language: string; text: string }
export type VerseDetail = {
  surah: number
  ayah: number
  surah_name_arabic: string
  surah_name_english: string
  arabic_text: string
  translation_en: string
  tafsirs: VerseTafsir[]
}

export type QuranVerseResult = {
  surah: { number: number; name_arabic: string; name_english: string }
  number: number
  text: string
}

export type HadithResult = {
  id: number
  slug: string
  collection_name_arabic: string
  collection_name_english: string
  number: number | null
  text: string
  translation_en: string
  narrator_chain: string
  grade: string
  arabic_bab_name: string
  english_bab_name: string
  english_grade: string
}

type VerseDetailResponse = {
  surah: { number: number; name_arabic: string; name_english: string }
  number: number
  text: string
  translation_en: string
  tafseer: Record<string, VerseTafsir>
}

async function contentFetch<T>(path: string): Promise<T> {
  const response = await fetch(`${CONTENT_API}${path}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(`Dawwin content request failed (${response.status})`)
  return response.json() as Promise<T>
}

export async function getVerseDetail(surah: number, ayah: number): Promise<VerseDetail> {
  const raw = await contentFetch<VerseDetailResponse>(`/quran/verses/${surah}:${ayah}/`)
  return {
    surah: raw.surah.number,
    ayah: raw.number,
    surah_name_arabic: raw.surah.name_arabic,
    surah_name_english: raw.surah.name_english,
    arabic_text: raw.text,
    translation_en: raw.translation_en,
    tafsirs: Object.values(raw.tafseer || {}),
  }
}

export async function lookupQuranVerse(reference: string): Promise<QuranVerseResult> {
  const ref = reference.trim()
  if (!/^\d{1,3}:\d{1,3}$/.test(ref)) throw new Error('Use a reference like 2:255')
  return contentFetch<QuranVerseResult>(`/quran/search/?verse=${encodeURIComponent(ref)}`)
}

export async function getQuranRange(surah: number, fromAyah: number, toAyah: number): Promise<{surah: QuranVerseResult['surah']; from_ayah:number; to_ayah:number; text:string}> {
  if (fromAyah === toAyah) {
    const verse = await lookupQuranVerse(`${surah}:${fromAyah}`)
    return { surah: verse.surah, from_ayah: verse.number, to_ayah: verse.number, text: verse.text }
  }
  return contentFetch(`/quran/verses/range/?from=${encodeURIComponent(`${surah}:${fromAyah}`)}&to=${encodeURIComponent(`${surah}:${toAyah}`)}`)
}

export function getHadithByRef(slug: string, number: number): Promise<HadithResult[]> {
  return contentFetch<HadithResult[]>(`/hadith/search/?ref=${encodeURIComponent(`${slug}:${number}`)}`)
}
